"use client";

import { useState, useEffect } from "react";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

function formatDate(date: Date | undefined) {
  if (!date) {
    return "";
  }

  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function isValidDate(date: Date | undefined) {
  if (!date) {
    return false;
  }
  return !isNaN(date.getTime());
}

type Props = {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  timeOfDay?: "Ujutro" | "Popodne" | "";
};

export function DatePicker({ value, onChange, timeOfDay }: Props) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(
    value || new Date("2025-06-01"),
  );
  const [, setMonth] = useState<Date | undefined>(date);
  const [inputValue, setInputValue] = useState(value ? formatDate(value) : "");

  useEffect(() => {
    if (value) {
      setDate(value);
      setInputValue(formatDate(value));
    } else {
      setDate(undefined);
      setInputValue("");
    }
  }, [value]);

  useEffect(() => {
    if (date && timeOfDay) {
      const dayOfWeek = date.getDay();
      const isValidDay =
        (timeOfDay === "Ujutro" && [4, 5, 6].includes(dayOfWeek)) ||
        (timeOfDay === "Popodne" && [2, 3].includes(dayOfWeek));

      if (!isValidDay) {
        setDate(undefined);
        setInputValue("");
        onChange?.(undefined);
      }
    }
  }, [timeOfDay, date, onChange]);

  return (
    <div className="relative flex gap-2 w-full">
      <Input
        id="date"
        value={inputValue}
        placeholder="Izaberite datum"
        className="bg-background pr-10 py-8"
        onChange={(e) => {
          const newDate = new Date(e.target.value);
          setInputValue(e.target.value);
          if (isValidDate(newDate)) {
            setDate(newDate);
            setMonth(newDate);
            onChange?.(newDate);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
          }
        }}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date-picker"
            variant="ghost"
            className="absolute top-1/2 right-2 size-6 -translate-y-1/2"
          >
            <CalendarIcon className="size-3.5" />
            <span className="sr-only">Select date</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto overflow-hidden p-0 shadow-none"
          align="end"
          alignOffset={-8}
          sideOffset={12}
        >
          <Calendar
            mode="single"
            defaultMonth={new Date()}
            numberOfMonths={1}
            selected={date}
            onSelect={(newDate) => {
              if (newDate) {
                setDate(newDate);
                setInputValue(formatDate(newDate));
                setOpen(false);
                onChange?.(newDate);
              }
            }}
            disabled={(date) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const checkDate = new Date(date);
              checkDate.setHours(0, 0, 0, 0);

              // Disable past dates
              if (checkDate <= today) {
                return true;
              }

              const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

              // If timeOfDay is selected, filter days based on working hours
              if (timeOfDay === "Ujutro") {
                // Ujutro: Only Četvrtak (4), Petak (5), Subota (6) - 08:00 - 16:00
                // Disable all other days (including Sun, Mon, Tue, Wed)
                return ![4, 5, 6].includes(dayOfWeek);
              } else if (timeOfDay === "Popodne") {
                // Popodne: Only Utorak (2), Srijeda (3) - 12:00 - 20:00
                // Disable all other days (including Sun, Mon, Thu, Fri, Sat)
                return ![2, 3].includes(dayOfWeek);
              }

              // If no timeOfDay selected, disable Sunday (0) and Monday (1) - Zatvoreno
              // But allow other days to be selectable
              return [0, 1].includes(dayOfWeek);
            }}
            className="shadow-none border-none [--cell-size:--spacing(10)] md:[--cell-size:--spacing(10)]"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
